import React, { useState } from 'react';
import { FileText, Eye, Download, Files, FileArchive, Loader2, Smartphone, QrCode, Link2, Send, ChevronDown } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import { Button } from '../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';

const API = process.env.REACT_APP_BACKEND_URL || '';
const pdfUrl = (pdf) => (pdf.startsWith('http') ? pdf : `${API}${pdf}`);
export const hasPdf = (t) => !!t?.pdf && t.pdf.startsWith('/api/extension/pdf-file/');

const BILLET_META = {
  MOBILE: { icon: Smartphone, label: 'MOBILE', cls: 'border-sky-400/60 text-sky-400' },
  QR: { icon: QrCode, label: 'QR', cls: 'border-emerald-400/60 text-emerald-400' },
  LIEN: { icon: Link2, label: 'LIEN', cls: 'border-violet-400/60 text-violet-400' },
  TRANSFERT: { icon: Send, label: 'TRANSFERT', cls: 'border-pink-400/60 text-pink-400' },
  PDF: { icon: FileText, label: 'PDF', cls: 'border-[#D4AF37]/60 text-[#D4AF37]' },
};

const shortDate = (iso) => {
  const d = new Date(iso);
  return isNaN(d) ? '' : d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' });
};

export const billetPending = (t) =>
  (hasPdf(t) && t.pdf_provisoire) || (!hasPdf(t) && !!t.billet_dispo_le && new Date(t.billet_dispo_le) > new Date());

export const billetKind = (t) => (hasPdf(t) ? (t.pdf_provisoire ? 'PROV' : 'PDF') : t.billet_type || 'AUCUN');

const BilletBadge = ({ ticket }) => {
  const m = BILLET_META[ticket.billet_type] || BILLET_META.LIEN;
  const Icon = m.icon;
  const pending = billetPending(ticket);
  const lines = (ticket.billet_info || '').split('\n').filter(Boolean);
  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            onClick={(e) => { e.stopPropagation(); if (ticket.billet_lien) window.open(ticket.billet_lien, '_blank', 'noopener'); }}
            data-testid={`ticket-billet-badge-${ticket.id}`}
            className={`inline-flex items-center gap-1 h-6 px-2 rounded border text-xs font-semibold whitespace-nowrap transition-colors hover:bg-white/5 ${pending ? 'border-orange-400/60 text-orange-400' : m.cls}`}
          >
            <Icon className="h-3 w-3" />
            {m.label}
            {pending && <span className="font-normal">· {shortDate(ticket.billet_dispo_le)}</span>}
          </button>
        </TooltipTrigger>
        <TooltipContent side="left" className="max-w-xs text-xs leading-relaxed" data-testid={`ticket-billet-info-${ticket.id}`}>
          <p className="font-bold mb-1">{m.label}{pending ? ` — dispo le ${new Date(ticket.billet_dispo_le).toLocaleString('fr-FR')}` : ''}</p>
          {lines.map((l, i) => <p key={i}>{l}</p>)}
          {ticket.billet_lien && <p className="mt-1 opacity-70">Clic : ouvrir le billet sur le compte</p>}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

const BILLET_FILTERS = [
  ['all', 'Tous'],
  ['pending', 'Pas encore disponibles'],
  ['PDF', 'PDF'],
  ['PROV', 'PDF provisoire'],
  ['MOBILE', 'Mobile (app)'],
  ['QR', 'QR code'],
  ['LIEN', 'Lien'],
  ['TRANSFERT', 'Transfert'],
  ['AUCUN', 'Sans billet'],
];

export const matchBilletFilter = (t, f) => f === 'all' || (f === 'pending' ? billetPending(t) : billetKind(t) === f);

export const BilletFilterHeader = ({ value, onChange }) => (
  <DropdownMenu>
    <DropdownMenuTrigger asChild>
      <button className="flex items-center gap-1 hover:text-[#C4A052] transition-colors" data-testid="billet-filter-trigger">
        <span className={value !== 'all' ? 'text-[#C4A052]' : ''}>{value === 'all' ? 'BILLET' : (BILLET_FILTERS.find(([k]) => k === value) || [])[1]}</span>
        <ChevronDown className="h-3 w-3" />
      </button>
    </DropdownMenuTrigger>
    <DropdownMenuContent align="center" className="w-52">
      {BILLET_FILTERS.map(([k, label]) => (
        <DropdownMenuItem key={k} data-testid={`billet-filter-${k.toLowerCase()}`} onClick={() => onChange(k)} className={value === k ? 'bg-zinc-700' : ''}>
          {label}
        </DropdownMenuItem>
      ))}
    </DropdownMenuContent>
  </DropdownMenu>
);

export const PdfCell = ({ ticket }) => {
  if (!hasPdf(ticket)) return ticket.billet_type ? <BilletBadge ticket={ticket} /> : <span className="text-xs text-zinc-500">-</span>;
  const prov = ticket.pdf_provisoire;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          onClick={(e) => e.stopPropagation()}
          data-testid={`ticket-pdf-btn-${ticket.id}`}
          title={ticket.billet_info || (prov ? 'PDF provisoire (billet définitif pas encore disponible)' : 'Billet PDF')}
          className={`inline-flex items-center gap-1 h-6 px-2 rounded border text-xs font-semibold transition-colors ${prov ? 'border-orange-400/60 text-orange-400' : 'border-[#D4AF37]/60 text-[#D4AF37]'} hover:bg-[#D4AF37]/10`}
        >
          <FileText className="h-3 w-3" />
          {prov ? 'PROV.' : 'PDF'}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="center" className="w-40" onClick={(e) => e.stopPropagation()}>
        <DropdownMenuItem data-testid={`ticket-pdf-view-${ticket.id}`} onClick={() => window.open(pdfUrl(ticket.pdf), '_blank', 'noopener')}>
          <Eye className="h-4 w-4 mr-2" /> Voir
        </DropdownMenuItem>
        <DropdownMenuItem data-testid={`ticket-pdf-download-${ticket.id}`} onClick={() => { window.location.href = `${pdfUrl(ticket.pdf)}?download=1`; }}>
          <Download className="h-4 w-4 mr-2" /> Télécharger
        </DropdownMenuItem>
        {ticket.billet_lien && (
          <DropdownMenuItem data-testid={`ticket-pdf-link-${ticket.id}`} onClick={() => window.open(ticket.billet_lien, '_blank', 'noopener')}>
            <Link2 className="h-4 w-4 mr-2" /> Lien d'origine
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

const saveBlob = (blob, name) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
};

export const BulkPdfButton = ({ tickets }) => {
  const [loading, setLoading] = useState(false);
  const withPdf = tickets.filter(hasPdf);

  const download = async (mode) => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/extension/pdf-bundle`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ticket_ids: withPdf.map((t) => t.id), mode }),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || `HTTP ${res.status}`);
      const stamp = new Date().toISOString().slice(0, 16).replace(/[:T]/g, '-');
      saveBlob(await res.blob(), mode === 'merged' ? `billets_${stamp}.pdf` : `billets_${stamp}.zip`);
    } catch (e) {
      window.alert(`Téléchargement impossible : ${e.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          disabled={loading || withPdf.length === 0}
          className="border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37]/20"
          data-testid="bulk-pdf-button"
        >
          {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
          Billets PDF ({withPdf.length})
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="center" className="w-56">
        <DropdownMenuItem data-testid="bulk-pdf-merged" onClick={() => download('merged')}>
          <Files className="h-4 w-4 mr-2" /> 1 PDF regroupé
        </DropdownMenuItem>
        <DropdownMenuItem data-testid="bulk-pdf-zip" onClick={() => download('zip')}>
          <FileArchive className="h-4 w-4 mr-2" /> PDF séparés (ZIP)
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
